/**
 * CloudOpenAIError — OpenAI-style error for the LLM module (#89098).
 *
 * Shape follows the OpenAI error object:
 *   { error: { message, type, param?, code? } }
 * Plus Genie BaaS extensions: status?, requestId?, retryAfterMs?.
 *
 * Uses the formal package's existing error philosophy: a single typed error
 * class for LLM operations (consistent with how `CloudError` serves the other
 * three modules), while remaining OpenAI-compatible for ergonomic consumption.
 */

export interface OpenAIErrorObject {
    message: string;
    type: string;
    param: string | null;
    code: string | null;
}

export interface CloudOpenAIErrorInit {
    /** HTTP response status code (undefined for in-stream errors). */
    status?: number;
    /** Server X-Request-Id header. */
    requestId?: string;
    /** Parsed Retry-After header in milliseconds. */
    retryAfterMs?: number;
    /** Underlying cause (e.g. original TypeError from fetch). */
    cause?: unknown;
}

/**
 * Error thrown by all LLM module operations.
 *
 * Both non-2xx HTTP responses and in-stream `event: error` events produce
 * this error. The `error` field always follows the OpenAI error object shape.
 */
export class CloudOpenAIError extends Error {
    readonly error: OpenAIErrorObject;
    readonly status?: number;
    readonly requestId?: string;
    readonly retryAfterMs?: number;

    constructor(
        error: OpenAIErrorObject | string,
        init: CloudOpenAIErrorInit = {},
    ) {
        const errorObj: OpenAIErrorObject =
            typeof error === 'string'
                ? { message: error, type: 'server_error', param: null, code: null }
                : error;
        super(errorObj.message);
        this.name = 'CloudOpenAIError';
        this.error = errorObj;
        this.status = init.status;
        this.requestId = init.requestId;
        this.retryAfterMs = init.retryAfterMs;
        if (init.cause !== undefined) {
            if (!('cause' in this)) {
                Object.defineProperty(this, 'cause', {
                    value: init.cause,
                    writable: false,
                    enumerable: false,
                    configurable: false,
                });
            }
        }
    }
}

/**
 * Parse Retry-After header value into milliseconds.
 *
 * Supports:
 * - Integer seconds: "120"
 * - HTTP-date: "Wed, 21 Oct 2026 07:28:00 GMT"
 * Returns undefined if the header is absent or unparseable.
 */
export function parseRetryAfter(headerValue: string | null): number | undefined {
    if (!headerValue) return undefined;

    const trimmed = headerValue.trim();

    // Try integer seconds (most common)
    const asNum = Number(trimmed);
    if (!isNaN(asNum) && asNum >= 0 && Number.isInteger(asNum)) {
        return asNum * 1000;
    }

    // Try HTTP-date
    const asDate = Date.parse(trimmed);
    if (!isNaN(asDate)) {
        const diff = asDate - Date.now();
        return diff > 0 ? diff : 0;
    }

    return undefined;
}
